import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Spinner from '@cloudscape-design/components/spinner';
import Box from '@cloudscape-design/components/box';
import { useAuth } from '../context/AuthContext';

/**
 * Props for the ProtectedRoute component.
 */
export interface ProtectedRouteProps {
  /** Child components to render if authentication checks pass */
  children: React.ReactNode;
  /** Whether this route requires admin role (default: false) */
  requireAdmin?: boolean;
}

/**
 * ProtectedRoute component that guards routes based on authentication and role.
 *
 * This component:
 * - Shows a loading indicator while auth state is being determined
 * - Redirects unauthenticated users to /login
 * - Redirects non-admin users to /dashboard if requireAdmin is true
 * - Renders children if all checks pass
 *
 * @example
 * ```tsx
 * // Protect a route that requires authentication
 * <Route
 *   path="/dashboard"
 *   element={
 *     <ProtectedRoute>
 *       <DashboardPage />
 *     </ProtectedRoute>
 *   }
 * />
 *
 * // Protect an admin-only route
 * <Route
 *   path="/admin/users"
 *   element={
 *     <ProtectedRoute requireAdmin>
 *       <AdminUserManagementPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 *
 * @param props - Component props
 * @returns Protected content or redirect
 *
 * @validates Requirements 13.1 - Redirects unauthenticated users to login
 * @validates Requirements 13.5 - Restricts admin pages to admin users only
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
}: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // Show loading indicator while auth state is being determined
  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
          Loading...
        </Box>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  // Preserve the intended destination in state for redirect after login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to dashboard if admin access is required but user is not admin
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // All checks passed, render the protected content
  return <>{children}</>;
}

export default ProtectedRoute;
